import { BrowserProvider } from "ethers";
import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import { useAuth } from "../contexts/AuthContext";
import { magic } from "../lib/magic";
import { placeParlayOnchain } from "../lib/onchain";
import LoadingDots from './LoadingDots';
import LoginModal from './LoginModal';
import './PurchaseModal.css';

interface ParlayLeg {
  eventTitle: string;
  marketQuestion: string;
  outcome: 'yes' | 'no';
  outcomePrice: number;
  decimalOdds: number;
  endDate?: string;
  marketIcon?: string;
  marketId: string;
}

interface CurrentQuote {
  quote_id: string;
  total_odds: number;
  expected_payout: number;
  expiry: string;
  total_amount: number;
}

interface ParlayConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentQuote: CurrentQuote | null;
  parlayLegs: ParlayLeg[];
  onConfirm: () => void;
  formatNumberWithCommas: (num: number) => string;
}

const ParlayConfirmationModal: React.FC<ParlayConfirmationModalProps> = ({
  isOpen,
  onClose,
  currentQuote,
  parlayLegs,
  onConfirm,
  formatNumberWithCommas
}) => {
  const { isAuthenticated, user } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isPlacing, setIsPlacing] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  async function getWalletAddressFallback(): Promise<string | undefined> {
    try {
      const provider = new BrowserProvider(magic.rpcProvider as any);
      const signer = await provider.getSigner();
      return await signer.getAddress();
    } catch {
      return undefined;
    }
  }

  function toEngineLegs(source: ParlayLeg[]) {
    return source.map(l => ({
      ingest_market_id: l.marketId,
      outcome_label: l.outcome.toLowerCase() === "yes" ? "Yes" : "No"
    }));
  }

  async function requestQuote(stake: number) {
    let walletAddr = user?.walletAddress || (await getWalletAddressFallback());
    
    // If gas sponsorship is available, use smart account address for quotes
    if (user?.gasSponsored && user?.smartAccountAddress) {
      walletAddr = user.smartAccountAddress;
      console.log('🚀 Using smart account address for quote:', walletAddr);
    }
    
    if (!walletAddr) throw new Error("No wallet address found.");
    if (!user?.issuer) throw new Error("Missing user issuer for quoting.");

    const payload = {
      issuer: user.issuer,
      smart_contract_wallet_address: walletAddr,
      stake,
      legs: toEngineLegs(parlayLegs)
    };

    const res = await fetch(`${API_URL}/quote`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`/quote failed: ${text}`);
    }
    return res.json();
  }

  const handleConfirm = async () => {
    setErrorMsg(null);
    setTxHash(null);

    if (!isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }

    if (!currentQuote) {
      setErrorMsg("No quote available");
      return;
    }

    let finalQuoteResp: any = null;
    
    try {
      setIsQuoting(true);
      const quoteResp = await requestQuote(currentQuote.total_amount);
      setIsQuoting(false);

      if (!quoteResp?.quote_struct || !quoteResp?.houseSig) {
        throw new Error("Backend did not return quote_struct/houseSig.");
      }

      const currentTimestamp = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = quoteResp.quote_struct.expiry - currentTimestamp;

      let placed: any = null;
      finalQuoteResp = quoteResp;

      if (timeUntilExpiry < 30) {
        const freshQuoteResp = await requestQuote(currentQuote.total_amount);
        finalQuoteResp = freshQuoteResp;
      }

      setIsPlacing(true);
      
      try {
        placed = await placeParlayOnchain(finalQuoteResp);
        if (placed?.txHash) setTxHash(placed.txHash);
      } catch (blockchainError: any) {
        // Don't record failed transactions - let the quote expire naturally
        // Better error messages for the user
        const errorMsg = blockchainError?.message || "Transaction failed";
        if (errorMsg.includes("user rejected") || errorMsg.includes("User denied")) {
          throw new Error("Transaction cancelled. You can try again.");
        } else if (errorMsg.includes("insufficient")) {
          throw new Error("Insufficient balance for transaction");
        } else if (errorMsg.includes("quote used")) {
          throw new Error("Quote already used. Please request a new quote.");
        } else if (errorMsg.includes("execution reverted")) {
          throw new Error("Transaction failed. Please try again.");
        } else if (blockchainError.name === 'GasSponsorshipError' || errorMsg.includes("Gas sponsorship failed")) {
          throw new Error("Gas sponsorship failed after multiple attempts. Please try again.");
        }
        throw blockchainError;
      }

      // Only record successful transactions
      await fetch(`${API_URL}/tickets/record`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quoteId: finalQuoteResp.quoteId,
          txHash: placed?.txHash || null
        })
      });

      onConfirm();
    } catch (e: any) {
      console.error(e);
      setErrorMsg(e?.message || "Failed to place parlay. Please try again.");
    } finally {
      setIsQuoting(false);
      setIsPlacing(false);
    }
  };

  const handleLoginSuccess = async () => {
    setIsLoginModalOpen(false);
    handleConfirm();
  };

  if (!isOpen || !currentQuote) return null;

  const isBusy = isQuoting || isPlacing;

  return (
    <div className="purchase-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="purchase-modal-content">
        <button 
          className="close-purchase-modal-btn"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="purchase-modal-header">
          <div className="purchase-modal-title">
            <div className="purchase-modal-question">Complete Your Parlay</div>
            <div className="purchase-modal-selection">
              {parlayLegs.length} leg{parlayLegs.length !== 1 ? 's' : ''} selected
            </div>
          </div>
        </div>

        {/* Display Selected Legs */}
        <div className="selected-legs-section">
          {parlayLegs.map((leg, index) => (
            <div key={`${leg.marketId}-${index}`} className="leg-display">
              <div className="leg-display-header">
                <div className="leg-info">
                  <div className="leg-title">{leg.eventTitle}</div>
                  <div className="leg-question">{leg.marketQuestion}</div>
                </div>
                <div className="leg-outcome-price">
                  {Math.round(leg.outcomePrice * 100)}¢
                </div>
                <div className="leg-outcome-single">
                  <span className={`leg-outcome-badge ${leg.outcome}`}>
                    {leg.outcome === 'yes' ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Total Amount Display */}
        <div className="amount-section">
          <div className="amount-header">
            <label>Total Parlay Amount</label>
          </div>
          <div className="amount-display-static">
            ${formatNumberWithCommas(currentQuote.total_amount)}
          </div>
        </div>

        <div className="payout-section">
          <div className="payout-header">
            <div className="payout-label">To win 💸</div>
          </div>
          <div className="payout-value">
            ${formatNumberWithCommas(currentQuote.expected_payout)}
          </div>
        </div>

        {errorMsg && <div className="error-message">{errorMsg}</div>}

        <button 
          className="add-to-parlay-btn"
          disabled={isBusy}
          onClick={handleConfirm}
        >
          {isBusy ? <LoadingDots className="button-loading" /> : 'Confirm Parlay'}
        </button>
      </div>

      <LoginModal isOpen={isLoginModalOpen} onClose={handleLoginSuccess} />
    </div>
  );
};

export default ParlayConfirmationModal;